import type { AppVariant } from '@open-songster/core'
import type { VariantEnvironment } from '@open-songster/ui'
import { resolveAppVariant } from '@open-songster/ui'
import type { Component } from 'vue'
import type { Router } from 'vue-router'

/** What a variant module (`./variants/*`) hands back to be mounted. */
export interface VariantModule {
  App: Component
  createRouter: () => Router
}

export type VariantLoader = () => Promise<VariantModule>

export interface BootstrapOptions {
  /** One lazy `import()` per variant, so only the chosen one is downloaded. */
  loaders: Record<AppVariant, VariantLoader>
  /** Runs once the variant is loaded; the host does the actual `createApp`. */
  mount: (variant: AppVariant, module: VariantModule) => void | Promise<void>
  /** Injected in tests; defaults to the real window. */
  environment?: Partial<VariantEnvironment>
}

/**
 * Decides which front end this visit gets and loads only that one. Kept free
 * of Vue and DOM calls so the decision is testable without a browser.
 */
export async function bootstrap(options: BootstrapOptions): Promise<AppVariant> {
  const variant = resolveAppVariant(options.environment)
  const module = await options.loaders[variant]()
  await options.mount(variant, module)
  return variant
}
